/* eslint-disable react/no-danger */

import { useEffect, useRef } from 'react';
import type { ProductPageViewModel } from 'autogen/swagger/all';
import { useRegimeStore } from 'components/product/regime/shared/regimeStore';
import usePageContext from 'hooks/user/usePageContext';
import useRequestContext from 'hooks/user/useRequestContext';
import TrackingContext from 'modules/tracking/GA4/helpers/TrackingContext';
import { useShallow } from 'zustand/shallow';
import ChanelProductPage from './ChanelProductPage';
import DysonProductPage from './DysonProductPage';
import LightProductPage from './LightProductPage';
import ProductPage from './ProductPage';
import { createProductStore, ProductContext } from './shared/productStore';
import usePushRemarketingDataOnce from './shared/usePushRemarketingDataOnce';

/**
 * Since we have a lot of ProductPage Variations on the same route we render this component instead for now.
 * A better solution would probably be unique routes.
 */

type ProductPageRendererProps = { model: ProductPageViewModel };

function ProductPageRendererComponent({ model }: ProductPageRendererProps) {
    const { brandContext } = usePageContext();
    const { isClubMatasLightPdp } = useRequestContext();

    usePushRemarketingDataOnce(model.remarketingData);

    if (isClubMatasLightPdp)
        return (
            <>
                <LightProductPage model={model} />
                <script
                    type="application/ld+json"
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(model.productStructuredData) }}
                />
            </>
        );

    if (brandContext === 'chanel')
        return (
            <>
                <ChanelProductPage model={model} />
                <script
                    type="application/ld+json"
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(model.productStructuredData) }}
                />
            </>
        );

    if (brandContext === 'dyson')
        return (
            <>
                <DysonProductPage model={model} />
                <script
                    type="application/ld+json"
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(model.productStructuredData) }}
                />
            </>
        );

    return (
        <>
            <ProductPage model={model} />
            <script
                type="application/ld+json"
                // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                dangerouslySetInnerHTML={{ __html: JSON.stringify(model.productStructuredData) }}
            />
        </>
    );
}

export default function ProductPageRenderer(props: ProductPageRendererProps) {
    const { model } = props;
    const { productInformation, productRegime } = model;
    const { productId, expectedDelivery } = productInformation;
    const { isSubscriptionSelected } = expectedDelivery?.subscriptionPriceInfoViewModel || {};
    const { setRegimeModel } = useRegimeStore(useShallow(({ setRegimeModel }) => ({ setRegimeModel })));
    const productStore = useRef(createProductStore({ productId, isSubscription: isSubscriptionSelected || false }));

    useEffect(() => {
        if (productRegime) {
            setRegimeModel(productRegime);
        }
    }, [setRegimeModel, productRegime]);

    return (
        <ProductContext.Provider value={productStore.current}>
            <TrackingContext context="product_page">
                <ProductPageRendererComponent {...props} />
            </TrackingContext>
        </ProductContext.Provider>
    );
}
